import { DataTable } from "primereact/datatable";
import PageContent from "../../components/PageContent";
import PageTitle from "../../components/PageTitle";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import useApi from "../../hooks/ApiHook";
import { InputText } from "primereact/inputtext";
import useToast from "../../hooks/ToastHook";
import { useNavigate } from "react-router-dom";
import InputSearchSidebar from "../../components/InputSearchSidebar";
import { DataView, DataViewLayoutOptions } from 'primereact/dataview';
import { classNames } from "primereact/utils";

const currencyIntl = Intl.NumberFormat('pt-br', { style: 'currency', currency: 'BRL' });

export default function() {

    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const [ products, setProducts ] = useState([]);
    const [ selectedToEdit, setSelectedToEdit ] = useState();
    const [ selectProductsVisible, setSelectProductsVisible ] = useState(false);
    const [ client, setClient ] = useState();
    const [ saving, setSaving ] = useState(false);

    useEffect(() => {
        if (selectedToEdit) {
            setSelectedToEdit(products.find(i => i.id === selectedToEdit.id));
        }
    }, [products]);

    const putProduct = (product) => {
        const index = products.findIndex(i => i.id === product.id);
        products[index] = product;
        setProducts([ ...products ]);
    }

    const removeProduct = (product) => {
        setProducts(products.filter(i => i.id !== product.id));
    }

    const changeUnit = product => {
        const units = product.units;
        const actual = product.unit;
        const index = product.units.findIndex(i => i === actual);
        putProduct({ ...product, unit: units[index + 1 >= units.length ? 0 : index + 1] })
    }

    const onSelectProduct = product => {
        if (products.some(p => p.id === product.id)) {
            return;
        }
        setProducts([ ...products, { ...product, availableQuantity: product.quantity, quantity: null } ])
    }

    const save = () => {
        setSaving(true);

        const productsPayload = products.map(p => ({
            product: {
                id: p.id,
            },
            quantity: p.quantity,
            unit: { symbol: p.unit },
            price: p.price
        }));

        const payload = {
            client: !!client ? { id: client.id } : null,
            items: productsPayload
        }

        api.post('orders', payload).then(() => {
            toast.showSuccess('Pedido criado com sucesso');
            navigate(-1);
        }).catch(e => {
            toast.showAxiosError(e);
        }).finally(() => {
            setSaving(false);
        });
    }

    const quantityEditor = product => (
        <div className="p-inputgroup">
            <InputNumber 
                value={product.quantity} 
                onChange={e => putProduct({ ...product, quantity: (e.value || 0) })}
                maxFractionDigits={5}
                locale="pt-BR"/>
            <span className="p-inputgroup-addon" onClick={() => changeUnit(product)}>
                {product.unit}
            </span>
        </div>
    );

    const mobileViewTemplate = products => {
        if (!products || products.length === 0) return (
            <div className="py-4">
                Adicione produtos ao pedido
            </div>
        );
        return products.map((product, index) => (
            <div className="col-12 cursor-pointer" key={product.id} onClick={() => setSelectedToEdit(product)}>
                <div className={classNames('flex flex-column xl:flex-row xl:align-items-start py-3 gap-2', { 'border-top-1 surface-border': index !== 0 })}>
                    <div className={classNames("font-bold", { 'text-primary': selectedToEdit === product })}>{product.description}</div>
                    <div className="flex justify-content-between">
                        <div>Qtd: {product.quantity || 0} {product.unit}</div>
                        <i className="pi pi-pencil"></i>
                    </div>
                </div>
            </div>
        ))
    }

    const productActions = product => (
        <div>
            <Button icon="pi pi-trash" title="Remover" onClick={() => removeProduct(product)} tabIndex={-1}/>
            {/* <Button icon="pi pi-pencil" title="Editar" className="ml-2 md:hidden" onClick={() => setSelectedToEdit(product)}/> */}
        </div>
    );

    const ClientDropdownItemTemplate = (client) => (
        <div>
            <div className="font-semibold">{client.commercialName}</div>
            <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{client.document}</div>
        </div>
    );

    const descriptionTemplate = p => (
        <>
            <div>{p.description}</div>
            <div className="text-xs font-semibold text-color-secondary">{p.owner.commercialName}</div>
        </>
    );

    return (
        <div>
            <PageTitle title="Novo pedido"/>
            <PageContent>
                <div className="flex justify-content-between flex-wrap gap-3 mb-3">
                    <div>
                        <InputSearchSidebar
                            title="Buscar cliente"
                            placeholder="Buscar cliente"
                            searchPath="clients"
                            searchParam="client.commercialName[contains]"
                            optionTemplate={ClientDropdownItemTemplate}
                            valueTemplate={client => client.commercialName}
                            getKey={client => client.id}
                            onChange={client => setClient(client)}
                            value={client}/>
                    </div>
                    <div>
                        <Button label="Selecionar produtos" className="mr-2" onClick={() => setSelectProductsVisible(true)}/>
                        <Button label="Finalizar" onClick={save} loading={saving}/>
                    </div>
                </div>
                <DataView className="lg:hidden" value={products} listTemplate={mobileViewTemplate}/>
                <DataTable className="hidden lg:block" value={products} selectionMode="single" emptyMessage="Adicione produtos ao pedido">
                    <Column body={descriptionTemplate} header="Produto"/>
                    <Column field="code" header="Código"/>
                    <Column body={p => p.availableQuantity + ' ' + p.unit} header="Disponível"/>
                    <Column body={quantityEditor} header="Quantidade" style={{width:'20%'}} className="md:table-cell" bodyStyle={{ display: 'none' }}/>
                    <Column body={productActions} header="Ações" align='right'></Column>
                </DataTable>
            </PageContent>
            <Sidebar visible={selectedToEdit} onHide={() => setSelectedToEdit(null)} position="right">
                <h2>Quantidade</h2>
                <div className="p-inputgroup">
                    <InputNumber 
                        value={selectedToEdit?.quantity} 
                        onChange={e => putProduct({ ...selectedToEdit, quantity: (e.value || 0) })}
                        maxFractionDigits={5}
                        locale="pt-BR"/>
                    <span className="p-inputgroup-addon" onClick={() => changeUnit(selectedToEdit)}>
                        {selectedToEdit?.unit}
                    </span>
                </div>
                <Button label="Fechar" className="w-full mt-2" onClick={() => setSelectedToEdit(null)}/>
                <Button label="Remover" className="w-full mt-2" severity="secondary" onClick={() => removeProduct(selectedToEdit)}/>
            </Sidebar>
            <ProductSelection 
                visible={selectProductsVisible}
                selectedProducts={products}
                onHide={() => setSelectProductsVisible(false)}
                onSelect={onSelectProduct}
                clientId={client?.id}/>
        </div>
    );
}

function ProductSelection({ onSelect, selectedProducts, visible, onHide, clientId }) {

    const api = useApi();
    const toast = useToast();
    const [ productList, setProductList ] = useState([]);
    const [ searchTerm, setSearchTerm ] = useState('');
    const [ loading, setLoading ] = useState(false);

    useEffect(() => {
        setLoading(true);
        const timeOutId = setTimeout(() => {
            api.get('products', { params: { search: searchTerm, "client.id": clientId } }).then(r => {
                setProductList(r.data.data);
            }).catch(e => {
                toast.showAxiosError(e);
            }).finally(() => {
                setLoading(false);
            });
        }, 500);
        return () => clearTimeout(timeOutId);
    }, [searchTerm, clientId]);

    return (
        <Sidebar visible={visible} onHide={onHide} position="right" className="md:w-30rem">
            <div className="h-full flex flex-column">
                <h2>Selecionar produtos</h2>
                <span className="p-input-icon-right w-full">
                    {loading && <i className="pi pi-spin pi-spinner" />}
                    <InputText
                        className="w-full" 
                        onChange={e => setSearchTerm(e.target.value)} 
                        value={searchTerm}/>
                </span>
                <div className="w-full flex-grow-1 mt-4 max-w-full flex-shrink-1 overflow-y-hidden overflow-y-scroll">
                    {productList.map(p => {
                        const selected = selectedProducts.some(s => s.id === p.id);
                        return (
                            <div className={`p-3 cursor-pointer hover:surface-hover ${selected && 'bg-primary-reverse'}`} onClick={() => onSelect(p)}>
                                <div className="font-semibold">{p.code}</div>
                                <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{p.description}</div>
                                <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{p.owner.commercialName}</div>
                                <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{p.eanCode}</div>
                                <div className="text-sm" style={{ maxWidth: 300, whiteSpace: 'break-spaces' }}>{p.quantity} {p.unit}</div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </Sidebar>
    );
}